/**
 * Puzzle generation: build a full solution, then dig holes.
 *
 * The rule the whole product rests on lives here -- a clue is only removed if
 * the board still has exactly one solution afterwards, checked by
 * `hasUniqueSolution` on every single removal. A puzzle that leaves this
 * function therefore never requires guessing.
 *
 * Difficulty targeting is not this module's job. It needs the technique solver,
 * so it lives in `grade.ts` and drives generation from the outside by asking for
 * puzzles until one grades into the wanted tier.
 */

import {
  CELLS,
  EMPTY,
  cloneBoard,
  clueCount,
  emptyBoard,
  type Board,
  type Digit,
} from "./grid";
import { CLASSIC, type ConstraintSet } from "./constraints";
import { computeCandidates, digitsOf, popcount } from "./candidates";
import { hasUniqueSolution } from "./solver-fast";
import { randomSeed, seededRng, shuffle, type Rng } from "./random";

export interface GeneratedPuzzle {
  readonly puzzle: Board;
  readonly solution: Board;
  readonly clues: number;
  readonly seed: number;
}

export interface GenerateOptions {
  /** Reuse a seed to reproduce a puzzle exactly (daily puzzles, test fixtures). */
  seed?: number;
  /** Remove clues in 180deg-rotational pairs. Prettier, but leaves more clues. */
  symmetric?: boolean;
  /** Stop digging once this many clues remain. Default: dig as far as possible. */
  minClues?: number;
  cs?: ConstraintSet;
}

/**
 * A random complete grid, by randomised backtracking with a most-constrained-cell
 * heuristic. Fast enough that the cost of generation is entirely in digging.
 */
export function randomFullGrid(rng: Rng, cs: ConstraintSet = CLASSIC): Board {
  const board = emptyBoard();

  const fill = (): boolean => {
    const candidates = computeCandidates(board, cs);

    let target = -1;
    let best = 10;
    for (let cell = 0; cell < CELLS; cell++) {
      if (board[cell] !== EMPTY) continue;
      const count = popcount(candidates[cell]!);
      if (count === 0) return false;
      if (count < best) {
        best = count;
        target = cell;
        if (count === 1) break;
      }
    }
    if (target === -1) return true; // nothing empty: grid complete

    for (const digit of shuffle(digitsOf(candidates[target]!), rng)) {
      board[target] = digit;
      if (fill()) return true;
      board[target] = EMPTY;
    }
    return false;
  };

  if (!fill()) throw new Error("failed to build a full grid");
  return board;
}

/**
 * Remove clues while the solution stays unique.
 *
 * Cells are visited in random order and a removal that would admit a second
 * solution is put straight back, so the result is minimal with respect to this
 * ordering (not globally minimal -- that would need search, and is not worth it).
 */
export function digHoles(
  solution: Board,
  rng: Rng,
  options: { symmetric?: boolean; minClues?: number; cs?: ConstraintSet } = {},
): Board {
  const cs = options.cs ?? CLASSIC;
  const minClues = options.minClues ?? 0;
  const puzzle = cloneBoard(solution);

  const order = shuffle(
    Array.from({ length: CELLS }, (_, i) => i),
    rng,
  );

  let remaining = CELLS;
  for (const cell of order) {
    if (remaining <= minClues) break;
    if (puzzle[cell] === EMPTY) continue;

    const group = options.symmetric && cell !== CELLS - 1 - cell ? [cell, CELLS - 1 - cell] : [cell];
    const saved = group.map((c) => puzzle[c]!);
    if (saved.some((v) => v === EMPTY)) continue;
    if (remaining - group.length < minClues) continue;

    for (const c of group) puzzle[c] = EMPTY;
    if (hasUniqueSolution(puzzle, cs)) {
      remaining -= group.length;
    } else {
      group.forEach((c, i) => {
        puzzle[c] = saved[i] as Digit;
      });
    }
  }

  return puzzle;
}

export function generate(options: GenerateOptions = {}): GeneratedPuzzle {
  const seed = options.seed ?? randomSeed();
  const cs = options.cs ?? CLASSIC;
  const rng = seededRng(seed);

  const solution = randomFullGrid(rng, cs);
  const puzzle = digHoles(solution, rng, {
    symmetric: options.symmetric,
    minClues: options.minClues,
    cs,
  });

  return { puzzle, solution, clues: clueCount(puzzle), seed };
}
